import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import UndoIcon from '@mui/icons-material/Undo';
import { Avatar, Box, Chip, IconButton, Tooltip, Typography } from '@mui/material';
import { AgGridReact } from 'ag-grid-react';
import PropTypes from 'prop-types';
import { useCallback, useMemo, useRef } from 'react';

import colors from 'common/constants/colors';
import SYSTEM_DEFICIT from 'common/constants/inventoryLabels';
import {
  buildGroupPlaceholderRows,
  isDispensedOrCountLocked,
  reviewGroupFor,
} from '../utils/bulkLotNdcReviewGroups';
import { ROW_STATUS, UNUSABLE_REASON, formatQty, isOffPreference } from '../utils/bulkLotNdcStatus';
import { STATUS_COLOR, statusColorFor, statusLabelFor } from '../utils/bulkLotNdcStatusLabels';
import { warningTextTone, warningTint, warningTone } from '../utils/warningTone';
import {
  GROUPED_GRID_SX,
  GROUP_ROW_RENDERER_PARAMS,
  REVIEW_GROUP_COLUMN,
  ROW_CLASS_RULES,
  ReviewGroupPlaceholderRow,
  ReviewGroupSummaryContext,
  compareReviewGroupNodes,
  isGroupPlaceholderRow,
  isReviewGroupOpenByDefault,
  revealRecentlyMovedRow,
  reviewRowHeight,
} from './BulkLotNdcReviewGroupRows';

/**
 * POM-6010 — the Review Lot & NDC grid.
 *
 * AG Grid, configured the same way the Inventory Dashboard's grid is (navy header, resizable
 * columns, Columns/Filters side rails), so the two inventory-facing screens read as one product
 * rather than two. Every module it needs is already registered globally in `src/index.js`.
 *
 * It derives nothing about inventory: the status, reason, editability and blocking decision for
 * each row arrive already decided by `classifyRows`. This file turns those into flat row data —
 * flat because AG Grid's own sorting and filtering only see the values on the row, so anything
 * nested would be invisible to the side rails.
 */

/**
 * 'YYYY-MM-DD' -> 'MM/DD/YYYY', by string surgery rather than `new Date()`.
 *
 * Core normalises every expiry to a date-only string, and putting it through a Date would
 * reinterpret it as UTC midnight — which renders as the PREVIOUS day for anyone west of GMT. An
 * expiry date is the one field on this screen that must not be off by one.
 */
export const formatExpiry = (value) => {
  if (!value) return '';
  const [year, month, day] = String(value).slice(0, 10).split('-');

  return year && month && day ? `${month}/${day}/${year}` : '';
};

/**
 * 'Smartscripts - IA' -> 'I', for the facility avatar.
 *
 * One character, not two: the badge is a 26px circle and the state code sat wall to wall in it,
 * reading as a cramped label rather than a mark. It is not the identifier anyway — the column
 * header, the tooltip and the Filters rail all carry the full name, so the avatar only has to
 * tell one facility from another at a glance down the column.
 *
 * Still taken from the suffix rather than the leading character, so every 'Smartscripts - XX'
 * pharmacy does not collapse to the same 'S'.
 */
export const facilityShortName = (name) => {
  if (!name) return '?';
  const tail = name.split('-').pop().trim();

  return (tail || name).slice(0, 1).toUpperCase();
};

/** The second line under the lot number: why this lot is or is not printable. */
export const lotDetailFor = ({ row, change }) => {
  const { item, status, printsWithoutLot, unusableReason, unusableLot, recordsUnverifiedLot } = row;

  // POM-6121: told apart from an ordinary pending edit, because what commits is not the same
  // thing — a claim against stock inventory never received, rather than a lot swap.
  if (change) return recordsUnverifiedLot ? 'Hand-recorded · pending commit' : 'Pending commit';

  // POM-6119: the lot the classifier judged, not simply the first one — on a multi-lot fill they
  // are different lots, and this line states quantities and dates that belong to one of them.
  const [firstRecorded] = item.recordedLots ?? [];
  const recorded = unusableLot ?? firstRecorded;
  if (!recorded) return printsWithoutLot ? 'Prints without a lot number' : '';

  const expiry = formatExpiry(recorded.expirationDate);
  // POM-6121: quantity already claimed against this lot and never received. Worth saying on the
  // row, because a further hand-recorded claim on top of it is then a visible decision.
  const unverified =
    Number(recorded.unverifiedQty) > 0
      ? ` · ${formatQty(recorded.unverifiedQty)} ${SYSTEM_DEFICIT.toLowerCase()}`
      : '';
  const isUnusable = status === ROW_STATUS.UNUSABLE_LOT || status === ROW_STATUS.NO_USABLE_LOT;

  if (!isUnusable) return expiry ? `Exp ${expiry}${unverified}` : unverified.slice(3);

  let lead = 'No longer in inventory';
  if (unusableReason === UNUSABLE_REASON.EXPIRED) {
    lead = expiry ? `Expired ${expiry}` : 'Expired';
  } else if (unusableReason === UNUSABLE_REASON.SHORT_FOR_FILL) {
    // POM-6119: a lot holding part of the fill read as "Out of stock", which is the one thing it
    // is not — and the operator's next move depends on knowing there is stock to dispense.
    lead = `Holds ${formatQty(recorded.remainingQty)} of ${formatQty(item.fillQuantity)}`;
  } else if (unusableReason === UNUSABLE_REASON.OUT_OF_STOCK) {
    lead = 'Out of stock';
  }

  return printsWithoutLot ? `${lead} · prints without a lot number` : lead;
};

/**
 * Flatten the classified rows into AG Grid row data.
 *
 * `row` is carried through so the cell renderers can hand the whole thing back to the edit
 * dialog, which needs the item and its availability, not the flattened display strings.
 */
export const buildRowData = ({ visibleRows, changes, recentlyMovedIds = new Set() }) =>
  visibleRows.map((row) => {
    const { item, status, canEdit, reason, unusableReason } = row;
    const change = changes[item.requestLineItemId];
    const recorded = item.recordedLots ?? [];
    const lots = change
      ? change.lotAllocations.map((lot) => lot.lotNumber)
      : recorded.map((lot) => lot.lotNumber);

    // A multi-lot fill says how much of the quantity each lot covers, because the v1 editor can
    // only replace the whole thing with a single lot.
    const allocatedAcrossLots = recorded.reduce((sum, lot) => sum + Number(lot.quantity), 0);
    const statusColor = statusColorFor(row);
    // POM-6534: read-only rows carry a neutral stripe so they read as settled, not as blank.
    const readOnlyStripe = isDispensedOrCountLocked(status) ? colors.reviewReadOnlyStripe : '';

    return {
      id: item.requestLineItemId,
      orderId: item.orderId ?? '',
      rxNumber: item.rxNumber ?? '',
      facility: item.fulfillingPharmacyName ?? '',
      facilityShort: facilityShortName(item.fulfillingPharmacyName),
      drug: `${item.medicationName ?? ''}${item.strength ? ` ${item.strength}` : ''}`.trim(),
      qty: formatQty(item.fillQuantity),
      partner: item.partner ?? '',
      ndc: change?.ndc ?? item.effectiveNdc ?? '—',
      // Advisory, and independent of `status` on purpose: an off-preference row is printable and
      // is never excluded by it. Computed for untouched rows too, so drift that predates this
      // session is visible on open rather than only after someone edits the row.
      offPreference: isOffPreference({ item, change }),
      preferredNdc: item.partnerPreferredNdc ?? '',
      lots: lots.length > 0 ? lots.join(', ') : '—',
      // POM-6536: a shared lot is worded from the whole batch, which the row's own lot cannot say.
      lotDetail: row.batchLotDetail || lotDetailFor({ row, change }),
      // The unusable lot number reads as rejected rather than current: it is what the label would
      // have carried.
      // POM-6119: a short lot is struck through nowhere — it is the lot that will be dispensed,
      // and crossing it out says the opposite of what the row now does.
      struck:
        status === ROW_STATUS.UNUSABLE_LOT && unusableReason !== UNUSABLE_REASON.SHORT_FOR_FILL,
      splitCaption:
        !change && recorded.length > 1
          ? `${formatQty(allocatedAcrossLots)} of ${formatQty(item.fillQuantity)} across ${
              recorded.length
            } lots`
          : '',
      status,
      statusLabel: statusLabelFor(row),
      // Derived here rather than in the cell so the colour cannot disagree with the label beside
      // it: both read the classified row, and a cell given a flat field can only render it.
      statusColor,
      stripeColor: statusColor ?? readOnlyStripe,
      reviewGroup: reviewGroupFor(status),
      isRecentlyMoved: recentlyMovedIds.has(item.requestLineItemId),
      isGroupPlaceholder: false,
      reason,
      canEdit,
      // POM-6536: a staged claim is undone like a change.
      isChanged: !!change || !!row.isClaimPending,
      // POM-6121: this row's staged edit is a hand-recorded claim. Read off the classified row
      // rather than re-derived from the change, so the badge cannot disagree with the reason line
      // the classifier wrote beside it.
      isUnverified: !!row.recordsUnverifiedLot || !!row.claimsShortfall,
      row,
    };
  });

/**
 * The flat row the cells below receive. Declared field by field rather than as an open shape so
 * `react/prop-types` can actually check the cells — an open shape silently passes everything.
 */
const ROW_DATA_SHAPE = PropTypes.shape({
  id: PropTypes.string,
  rxNumber: PropTypes.string,
  facility: PropTypes.string,
  facilityShort: PropTypes.string,
  drug: PropTypes.string,
  ndc: PropTypes.string,
  offPreference: PropTypes.bool,
  preferredNdc: PropTypes.string,
  lots: PropTypes.string,
  lotDetail: PropTypes.string,
  splitCaption: PropTypes.string,
  struck: PropTypes.bool,
  status: PropTypes.string,
  statusLabel: PropTypes.string,
  statusColor: PropTypes.string,
  stripeColor: PropTypes.string,
  reviewGroup: PropTypes.string,
  isRecentlyMoved: PropTypes.bool,
  isGroupPlaceholder: PropTypes.bool,
  reason: PropTypes.string,
  canEdit: PropTypes.bool,
  isChanged: PropTypes.bool,
  isUnverified: PropTypes.bool,
  row: PropTypes.shape({}),
});

const TwoLineCell = ({ primary, secondary, sx }) => (
  // minWidth: 0 is load-bearing now the cell is a flex container: without it a flex item refuses
  // to shrink below its content width, so long secondary text clips instead of wrapping.
  <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
    <Typography variant='body2' sx={{ lineHeight: 1.3, ...sx }}>
      {primary}
    </Typography>
    {secondary ? (
      <Typography
        variant='caption'
        color='text.secondary'
        sx={{ lineHeight: 1.25, whiteSpace: 'normal' }}
      >
        {secondary}
      </Typography>
    ) : null}
  </Box>
);

TwoLineCell.propTypes = {
  primary: PropTypes.node,
  secondary: PropTypes.node,
  sx: PropTypes.shape({}),
};
TwoLineCell.defaultProps = { primary: null, secondary: null, sx: null };

export const FacilityCell = ({ data }) => (
  <Tooltip title={data?.facility ?? ''}>
    <Avatar
      sx={{
        width: 26,
        height: 26,
        fontSize: '0.65rem',
        fontWeight: 700,
        bgcolor: 'primary.main',
      }}
    >
      {data?.facilityShort}
    </Avatar>
  </Tooltip>
);

FacilityCell.propTypes = { data: ROW_DATA_SHAPE };
FacilityCell.defaultProps = { data: null };

export const DrugCell = ({ data }) => (
  <Box sx={{ whiteSpace: 'normal', lineHeight: 1.3, fontSize: '0.8125rem', minWidth: 0 }}>
    {data?.drug}
  </Box>
);

DrugCell.propTypes = { data: ROW_DATA_SHAPE };
DrugCell.defaultProps = { data: null };

/**
 * The NDC, and the edit affordance for the whole row.
 *
 * The pencil sits here rather than in a trailing actions column because NDC is what the user
 * changes first — the lot list they then pick from is a consequence of it.
 */
export const NdcCell = ({ data, onEdit, onUndo }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
    <Box component='span' sx={{ fontFamily: 'monospace', fontSize: '0.78rem', lineHeight: 1.3 }}>
      {data?.ndc}
    </Box>
    {/* Next to the NDC rather than in the status column, because it is a fact about this value
        and not about the row's printability — the status column must keep meaning "can this
        print". Tooltip carries the preferred NDC: the icon alone says something is off, the
        number says what to do about it. */}
    {data?.offPreference && (
      <Tooltip title={`Partner prefers ${data.preferredNdc}`}>
        <ReportProblemOutlinedIcon
          aria-label={`Not the partner's preferred NDC ${data.preferredNdc}`}
          /* warning.main is the brand navy in this theme, which made the flag invisible against
             the NDC next to it. See utils/warningTone. */
          sx={{ fontSize: '0.95rem', color: warningTone, flexShrink: 0 }}
        />
      </Tooltip>
    )}
    {data?.canEdit && (
      <Tooltip title='Change NDC & lot'>
        <IconButton
          size='small'
          aria-label={`Change NDC & lot for ${data.rxNumber || data.id}`}
          onClick={() => onEdit?.(data.row)}
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            width: 26,
            height: 26,
          }}
        >
          <EditOutlinedIcon sx={{ fontSize: '0.9rem' }} />
        </IconButton>
      </Tooltip>
    )}
    {data?.isChanged && (
      <Tooltip title='Undo this change'>
        <IconButton
          size='small'
          aria-label={`Undo change for ${data.rxNumber || data.id}`}
          onClick={() => onUndo?.(data.id)}
          sx={{ width: 26, height: 26 }}
        >
          <UndoIcon sx={{ fontSize: '0.9rem' }} />
        </IconButton>
      </Tooltip>
    )}
  </Box>
);

NdcCell.propTypes = {
  data: ROW_DATA_SHAPE,
  onEdit: PropTypes.func,
  onUndo: PropTypes.func,
};
NdcCell.defaultProps = { data: null, onEdit: null, onUndo: null };

export const LotCell = ({ data }) => (
  <TwoLineCell
    primary={data?.lots}
    secondary={data?.lotDetail || data?.splitCaption}
    sx={{
      textDecoration: data?.struck ? 'line-through' : 'none',
      color: data?.struck ? STATUS_COLOR[ROW_STATUS.UNUSABLE_LOT] : 'inherit',
      fontWeight: data?.struck ? 600 : 400,
    }}
  />
);

LotCell.propTypes = { data: ROW_DATA_SHAPE };
LotCell.defaultProps = { data: null };

export const StatusCell = ({ data }) => {
  const color = data?.statusColor;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
      {/* Two chips, not one status: POM-6121's hand-recorded row is CHANGED like any other edit,
          and separately as a deficit because of what that change posts. Folding the two together
          would have cost the filter dropdown a status it can offer, and lost the fact that such a
          row still commits and reprints exactly like the rest. */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
        <Chip
          size='small'
          label={data?.statusLabel}
          variant={color ? 'outlined' : 'filled'}
          sx={{
            alignSelf: 'flex-start',
            fontWeight: 600,
            fontSize: '0.78rem',
            height: 26,
            ...(color ? { color, borderColor: color } : {}),
          }}
        />
        {data?.isUnverified && (
          /* Amber from warningTone, not the theme's warning palette: that is the brand navy and
             would read as a second neutral chip beside the blue CHANGED one. */
          <Chip
            size='small'
            label={SYSTEM_DEFICIT.toUpperCase()}
            variant='outlined'
            sx={{
              alignSelf: 'flex-start',
              fontWeight: 700,
              fontSize: '0.72rem',
              height: 26,
              color: warningTextTone,
              borderColor: (theme) => warningTint(theme, 0.6),
              bgcolor: (theme) => warningTint(theme, 0.12),
            }}
          />
        )}
      </Box>
      {data?.reason ? (
        <Typography
          variant='caption'
          color='text.secondary'
          // AG Grid cells are nowrap+ellipsis by default, which cut these off mid-sentence
          // ("...pick an NDC an") — and the reason is the instruction, so it has to be readable.
          sx={{ fontSize: '0.8rem', lineHeight: 1.3, mt: 0.5, whiteSpace: 'normal' }}
        >
          {data.reason}
        </Typography>
      ) : null}
    </Box>
  );
};

StatusCell.propTypes = { data: ROW_DATA_SHAPE };
StatusCell.defaultProps = { data: null };

export const buildColumnDefs = ({ onEdit, onUndo }) => [
  { field: 'orderId', headerName: 'Order Id', width: 124, minWidth: 110 },
  { field: 'rxNumber', headerName: 'Rx Number', width: 104, minWidth: 96, pinned: 'left' },
  {
    field: 'facilityShort',
    // Spelled out: 'Fac' saved eleven pixels and cost the one word that says what the column is.
    // The width is set by the header now, not by the 26px badge it sits over.
    headerName: 'Facility',
    width: 92,
    minWidth: 92,
    // Repeats the default's two properties because a column-level cellStyle replaces it, and adds
    // horizontal centring: the avatar is a fixed 26px badge in a 74px column.
    cellStyle: { display: 'flex', alignItems: 'center', justifyContent: 'center' },
    cellRenderer: FacilityCell,
  },
  {
    field: 'drug',
    headerName: 'Drug name and strength',
    flex: 1.2,
    minWidth: 170,
    cellRenderer: DrugCell,
  },
  { field: 'qty', headerName: 'Qty', width: 74, minWidth: 74 },
  { field: 'partner', headerName: 'Partner', minWidth: 130 },
  {
    field: 'ndc',
    headerName: 'NDC',
    minWidth: 175,
    cellRenderer: NdcCell,
    cellRendererParams: { onEdit, onUndo },
  },
  { field: 'lots', headerName: 'Lot', flex: 1.4, minWidth: 200, cellRenderer: LotCell },
  {
    field: 'statusLabel',
    headerName: 'Status',
    // POM-6534: the widest column, so the reason wraps to two lines rather than three, and pinned
    // so the column the user is here to read cannot scroll off behind the side rail.
    width: 340,
    minWidth: 280,
    pinned: 'right',
    cellRenderer: StatusCell,
  },
];

export const DEFAULT_COL_DEF = {
  resizable: true,
  sortable: true,
  /**
   * Refresh a cell whenever its row's data is replaced, even when this column's own value is
   * unchanged.
   *
   * AG Grid's immutable-data path (`getRowId` + a new `rowData`) calls `rowNode.updateData`, which
   * refreshes only the cells whose value differs — `equals` is what it asks. Every renderer in this
   * file reads more of the row than its own value: NdcCell draws the pencil and the undo from
   * `canEdit`/`isChanged`, LotCell reads `lotDetail` and `struck`, StatusCell reads `status` and
   * `reason`. So picking another lot under the SAME NDC left the NDC cell untouched — the lot and
   * status cells updated, and the row's own undo button never appeared.
   *
   * One rule here rather than three `equals` on individual columns: the next renderer to read a
   * sibling field would otherwise be stale in exactly the same way, and the symptom (one cell of a
   * correctly-updated row silently a step behind) is close to undebuggable from the screen.
   *
   * Cheap because the grid is read-only and holds a batch, not a feed: the cost is re-rendering the
   * visible rows when the user applies a change, and no cell flashes (`enableCellChangeFlash` is
   * off and the update path passes `suppressFlash`).
   */
  equals: () => false,
  // POM-6534: the filter bar is the only filter, so the group counts always match the grid.
  filter: false,
  suppressHeaderMenuButton: true,
  wrapHeaderText: true,
  autoHeaderHeight: true,
  // Vertical centering belongs here, not in each renderer. Columns without a cellRenderer
  // (Order Id, Rx Number, Qty, Partner) use AG Grid's default cell, which top-aligns its text —
  // so in a 76px row they sat high while every custom renderer centred itself, and nothing in a
  // row lined up. One rule, applied to every column.
  //
  // NOTE: a column that sets its own cellStyle REPLACES this, so it has to repeat these two
  // properties or that column will fall out of alignment on its own.
  cellStyle: { display: 'flex', alignItems: 'center' },
};

// POM-6534: Columns only. The filter bar above narrows every group; a second filter system here
// would hide rows from it and make the group counts wrong.
const SIDE_BAR = {
  toolPanels: [
    {
      id: 'columns',
      labelDefault: 'Columns',
      labelKey: 'columns',
      iconKey: 'columns',
      toolPanel: 'agColumnsToolPanel',
      toolPanelParams: { suppressRowGroups: true, suppressValues: true },
    },
  ],
};

/**
 * A left stripe in the row's colour, so its state is readable without the Status column —
 * red/orange for rows that need a decision, green for a Recommended row that is good as it is,
 * grey for a row that is already dispensed or locked for count.
 */
const getRowStyle = ({ data }) => {
  const color = data?.stripeColor;

  return color ? { borderLeft: `4px solid ${color}` } : undefined;
};

const GRID_SX = {
  // Fills whatever the dialog gives it rather than a fixed viewport fraction: the dialog is now
  // height-constrained and a flex column, so the grid grows with the window instead of leaving
  // dead space above the bottom bar on a tall screen and overflowing on a short one.
  flex: 1,
  // Low enough that a one-row batch does not force a tall dialog of empty grid.
  minHeight: 280,
  width: '100%',
  '& .ag-header': { backgroundColor: 'primary.main' },
  '& .ag-header-cell-text': { color: 'primary.contrastText', fontWeight: 600 },
  '& .ag-header-icon': { color: 'primary.contrastText' },
  '& .ag-header-cell-resize::after': { backgroundColor: 'rgba(255,255,255,0.45)', width: '2px' },
  ...GROUPED_GRID_SX,
};

const BulkLotNdcReviewTable = ({
  visibleRows,
  changes,
  onEdit,
  onUndo,
  groupSummaries,
  recentlyMovedIds,
}) => {
  const rowData = useMemo(
    () => [
      ...buildRowData({ visibleRows, changes, recentlyMovedIds }),
      ...(groupSummaries ? buildGroupPlaceholderRows(groupSummaries) : []),
    ],
    [visibleRows, changes, recentlyMovedIds, groupSummaries]
  );
  const columnDefs = useMemo(
    () => [...buildColumnDefs({ onEdit, onUndo }), REVIEW_GROUP_COLUMN],
    [onEdit, onUndo]
  );

  const recentlyMovedRef = useRef(recentlyMovedIds);
  recentlyMovedRef.current = recentlyMovedIds;
  const revealedMovesRef = useRef(null);
  // Once per move: rowData also changes on every filter keystroke while the highlight is on, and
  // re-opening the group each time would pull the grid away from where the user is working.
  const revealMovedRow = useCallback(({ api }) => {
    if (revealedMovesRef.current === recentlyMovedRef.current) return;
    revealedMovesRef.current = recentlyMovedRef.current;
    revealRecentlyMovedRow({ api, rowId: [...recentlyMovedRef.current][0] });
  }, []);

  return (
    <Box sx={GRID_SX}>
      <ReviewGroupSummaryContext.Provider value={groupSummaries}>
        <AgGridReact
          rowData={rowData}
          columnDefs={columnDefs}
          defaultColDef={DEFAULT_COL_DEF}
          sideBar={SIDE_BAR}
          getRowId={({ data }) => data.id}
          getRowHeight={reviewRowHeight}
          groupDisplayType='groupRows'
          groupRowRendererParams={GROUP_ROW_RENDERER_PARAMS}
          isGroupOpenByDefault={isReviewGroupOpenByDefault}
          initialGroupOrderComparator={compareReviewGroupNodes}
          isFullWidthRow={isGroupPlaceholderRow}
          fullWidthCellRenderer={ReviewGroupPlaceholderRow}
          onRowDataUpdated={revealMovedRow}
          enableCellTextSelection
          tooltipShowDelay={400}
          getRowStyle={getRowStyle}
          rowClassRules={ROW_CLASS_RULES}
        />
      </ReviewGroupSummaryContext.Provider>
    </Box>
  );
};

BulkLotNdcReviewTable.propTypes = {
  visibleRows: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  changes: PropTypes.objectOf(PropTypes.shape({})).isRequired,
  onEdit: PropTypes.func.isRequired,
  onUndo: PropTypes.func.isRequired,
  groupSummaries: PropTypes.shape({
    isFiltered: PropTypes.bool,
    unverifiedQty: PropTypes.number,
    groups: PropTypes.objectOf(PropTypes.shape({})),
  }),
  recentlyMovedIds: PropTypes.instanceOf(Set),
};

BulkLotNdcReviewTable.defaultProps = {
  groupSummaries: null,
  recentlyMovedIds: new Set(),
};

export default BulkLotNdcReviewTable;
